import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, useSessionRenewal, useSessionSync, useSupportChat } from '@hooks';
import { ThemeProvider } from '@mui/system';
import GlobalStyles from '@mui/material/GlobalStyles';
import { cssVariables } from '@theme';
import { authActions } from '@actions';
import { authService } from '@services';
import { RoutePath } from '@constants/routes';
import { LANGUAGE, Languages } from '@constants/language';
import logger from '@widgets/logger';
import theme from '@theme';
// INFO: Features
import AuthRoute from '@features/AuthRoute';
import PrivateRoute from '@features/PrivateRoute';
import NotFoundPage from '@features/NotFoundPage';
import NavigationSync from '@features/NavigationSync';
import RedirectKeepingSearch from '@features/RedirectKeepingSearch';
import BumpVersion from '@features/BumpVersion';
import ImpersonationBanner from '@features/ImpersonationBanner';
import SEO from '@features/SEO';
// INFO: Pages
import ErrorPage from '@pages/Error';
import ErrorBoundary from '@features/ErrorBoundary';
import { Toaster } from '@components/Toaster';
import LoginPage from '@pages/Login';
import SignupPage from '@pages/Signup';
import ResetPassword from '@pages/ResetPassword';
import AcceptInvitation from '@pages/AcceptInvitation';
import ForgotPassword from '@pages/ForgotPassword';
import AppLayout from '@features/AppLayout';
import AccountSettingsPage from '@pages/AccountSettingsPage';
import FeedbacksPage from '@pages/FeedbacksPage';
import SurveyResponsePhotoPage from '@pages/SurveyResponsePhotoPage';
import ReviewsPage from '@pages/ReviewsPage';
import OnboardingPage from '@pages/OnboardingPage';
import VerifyEmail from '@pages/VerifyEmail';
import WidgetsPage from '@pages/WidgetsPage';
import ReplyAutomation from '@pages/ReplyAutomation';
import SurveysPage from '@pages/SurveysPage';
import ContactsPage from '@pages/ContactsPage';
import CampaignsPage from '@pages/CampaignsPage';
import HomePage from '@pages/HomePage';
import InboxPage from '@pages/InboxPage';
import MicrositesPage from '@pages/MicrositesPage';
import MenusPage from '@pages/MenusPage';
import FormsPage from '@pages/FormsPage';
import PostsPage from '@pages/PostsPage';
import ProfilesPage from '@pages/ProfilesPage';
import ProfilesMediaPage from '@pages/ProfilesMedia';
import PulseAI from '@pages/PulseAI';
import CouponsPage from '@pages/CouponsPage';
import LocationsManagePage from '@pages/LocationsManagePage';
import VisibilityScannerPage from '@pages/VisibilityScanner';
import AnalyticsLocationsRoute from '@pages/Analytics/AnalyticsLocationsRoute';
import SocialPostsAnalyticsPage from '@pages/Analytics/SocialPosts';
import ConversationsAnalyticsPage from '@pages/Analytics/Conversations';
import AnalyticsReviewsRoute from '@pages/Analytics/AnalyticsReviewsRoute';
import AnalyticsSurveysRoute from '@pages/Analytics/AnalyticsSurveysRoute';
// INFO: Temporary pages
//import OrdersPage from '@pages/OrdersPage';

const App = () => {
    const user = useSelector(state => state.auth.user);
    const authLoading = useSelector(state => state.auth.loading);

    const dispatch = useDispatch();
    const { i18n } = useTranslation();

    useSessionSync();
    useSessionRenewal(Boolean(user));
    useSupportChat(user, authLoading);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.token) {
            dispatch(authActions.getCurrentUser());
            sendLastVisit();
        } else {
            dispatch(authActions.setUserLoading(false));
        }
    }, [dispatch]);

    useEffect(() => {
        if (user?.language && user?.language !== i18n.language) {
            const language = Languages.includes(user?.language) ? user?.language : LANGUAGE.ENGLISH;
            i18n.changeLanguage(language);
        }
    }, [user?.language, i18n]);

    const sendLastVisit = async () => {
        try {
            await authService.updateLastVisit(new Date());
        } catch (error) {
            logger.error("Couldn't update last visit");
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <GlobalStyles styles={cssVariables} />
            <ImpersonationBanner />
            <BrowserRouter useTransitions={false}>
                <NavigationSync />
                <SEO />
                <ErrorBoundary>
                    <BumpVersion />
                    <Routes>
                        <Route
                            element={
                                <AppLayout>
                                    <Outlet />
                                </AppLayout>
                            }
                        >
                            <Route element={<PrivateRoute />}>
                                {/* <Route path="/orders" element={<OrdersPage />} /> */}
                                <Route path={RoutePath.Home} element={<HomePage />} />
                                {/* RoutePath.Surveys is necessary here to show the fckn paywall */}
                                <Route path={RoutePath.Surveys} element={<SurveysPage />} />
                                <Route path={RoutePath.SurveysResponses} element={<FeedbacksPage />} />
                                <Route path={RoutePath.SurveysResponsePhoto} element={<SurveyResponsePhotoPage />} />
                                <Route path={RoutePath.SurveysTemplates} element={<SurveysPage />} />
                                <Route path={`${RoutePath.SurveysAnalytics}/*`} element={<AnalyticsSurveysRoute />} />
                                <Route path={RoutePath.Microsites} element={<MicrositesPage />} />
                                <Route path={RoutePath.Menus} element={<MenusPage />} />
                                <Route path={RoutePath.Forms} element={<FormsPage />} />
                                <Route path={RoutePath.LocationsManage} element={<LocationsManagePage />} />
                                <Route path={RoutePath.LocationsProfiles} element={<ProfilesPage />} />
                                <Route path={RoutePath.LocationsMedia} element={<ProfilesMediaPage />} />
                                <Route
                                    path={RoutePath.LocationsVisibilityScanner}
                                    element={<VisibilityScannerPage />}
                                />
                                <Route
                                    path={`${RoutePath.LocationsAnalytics}/*`}
                                    element={<AnalyticsLocationsRoute />}
                                />
                                <Route path={RoutePath.ReviewsManage} element={<ReviewsPage />} />
                                <Route path={`${RoutePath.ReviewsAutomation}/*`} element={<ReplyAutomation />} />
                                <Route path={RoutePath.ReviewsWidgets} element={<WidgetsPage />} />
                                <Route path={`${RoutePath.ReviewsAnalytics}/*`} element={<AnalyticsReviewsRoute />} />
                                <Route path={`${RoutePath.AccountSettings}/*`} element={<AccountSettingsPage />} />
                                <Route path={RoutePath.MessagingInbox} element={<InboxPage />} />
                                <Route path={`${RoutePath.MessagingPulseAI}/*`} element={<PulseAI />} />
                                <Route path={RoutePath.MessagingAnalytics} element={<ConversationsAnalyticsPage />} />
                                <Route path={RoutePath.SocialPosts} element={<PostsPage />} />
                                <Route path={RoutePath.SocialAnalytics} element={<SocialPostsAnalyticsPage />} />
                                {/* RoutePath.Campaigns is necessary here to show the fckn paywall */}
                                <Route path={RoutePath.Campaigns} element={<CampaignsPage />} />
                                <Route path={RoutePath.CampaignsOneTime} element={<CampaignsPage />} />
                                <Route path={RoutePath.CampaignsAutomated} element={<CampaignsPage />} />
                                <Route path={RoutePath.CampaignsLibrary} element={<CampaignsPage />} />
                                <Route path={RoutePath.Contacts} element={<ContactsPage />} />
                                <Route path={RoutePath.Coupons} element={<CouponsPage />} />
                            </Route>
                            <Route
                                path={RoutePath.LocationsAnalyticsVisibilityLegacy}
                                element={<RedirectKeepingSearch to={RoutePath.LocationsVisibilityScanner} />}
                            />
                            {/* Redirects from base URL */}
                            <Route
                                path={`${RoutePath.Reviews}/*`}
                                element={<Navigate to={RoutePath.ReviewsManage} replace />}
                            />
                            <Route
                                path={`${RoutePath.Messaging}/*`}
                                element={<Navigate to={RoutePath.MessagingInbox} replace />}
                            />
                            <Route
                                path={`${RoutePath.Social}/*`}
                                element={<Navigate to={RoutePath.SocialPosts} replace />}
                            />
                            <Route
                                path={`${RoutePath.WebPages}/*`}
                                element={<Navigate to={RoutePath.Microsites} replace />}
                            />
                            <Route
                                path={RoutePath.Locations}
                                element={<Navigate to={RoutePath.LocationsManage} replace />}
                            />
                            <Route
                                path={`${RoutePath.PresenceProfiles}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.LocationsProfiles} />}
                            />
                            <Route
                                path={`${RoutePath.PresenceMedia}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.LocationsMedia} />}
                            />
                            <Route
                                path={`${RoutePath.PresenceAnalytics}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.LocationsAnalytics} />}
                            />
                            <Route
                                path={`${RoutePath.Presence}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.LocationsProfiles} />}
                            />
                            <Route
                                path={`${RoutePath.MicrositesLegacy}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.Microsites} />}
                            />
                            <Route
                                path={`${RoutePath.MenusLegacy}/*`}
                                element={<RedirectKeepingSearch to={RoutePath.Menus} />}
                            />
                            <Route
                                path={`${RoutePath.DigitalLegacy}/*`}
                                element={
                                    <RedirectKeepingSearch from={RoutePath.DigitalLegacy} to={RoutePath.WebPages} />
                                }
                            />
                        </Route>
                        <Route element={<PrivateRoute />}>
                            <Route path={RoutePath.Onboarding} element={<OnboardingPage />} />
                        </Route>
                        <Route element={<AuthRoute />}>
                            <Route path={RoutePath.Login} element={<LoginPage />} />
                            <Route path={RoutePath.Signup} element={<SignupPage />} />
                            <Route path={`${RoutePath.AcceptInvitation}/:token`} element={<AcceptInvitation />} />
                            <Route path={RoutePath.AcceptInvitation} element={<AcceptInvitation />} />
                        </Route>
                        <Route path={`${RoutePath.ResetPassword}/:token`} element={<ResetPassword />} />
                        <Route path={RoutePath.ForgotPassword} element={<ForgotPassword />} />
                        <Route path={`${RoutePath.VerifyEmail}/:token`} element={<VerifyEmail />} />
                        <Route path={RoutePath.Error} element={<ErrorPage />} />
                        <Route path={RoutePath.NotFound} element={<NotFoundPage />} />
                    </Routes>
                    <Toaster />
                </ErrorBoundary>
            </BrowserRouter>
        </ThemeProvider>
    );
};

export default App;
